import { css, StyleSheet } from 'aphrodite';
import { type FC, useRef } from 'react';
import {
	getContentHtmlStringValueFromRawRichTextContentState,
	type IRichContentEditorState,
} from '../../../extViewmodels';
import { createRichContentEditorStateWithText, ToolbarLight } from '../../../models/UiUtils';
import { RichContentDocumentEditor } from '../../../web/components/richContent/RichContentDocumentEditor';

export type NoteEditorProps = {
	/** HTML string. v1 was plain text; v2 interprets the value as HTML. */
	value: string;
	/** Fired with the editor's HTML on every change. */
	onChange: (value: string) => void;
	placeholder?: string;
	disabled?: boolean;
	minHeight?: number;
};

/**
 * Body CSS injected into the TinyMCE iframe. Without `contentRawCss` TinyMCE
 * falls back to its default `body` styles which leave the editable area
 * indistinguishable from an empty div — and (more materially for the click
 * regression) prevents the iframe body from sizing reliably when the
 * autoresize plugin manages height. Mirrors `DefaultAIDANoteContentCSS` from
 * the legacy `AidaNoteEditor`.
 */
const NOTE_EDITOR_CONTENT_CSS = `
	body {
		background: transparent;
		font-family: inherit;
		font-size: 14px;
		margin: 0;
		padding: 10px 14px;
	}
	p { margin: 0 0 8px; }
`;

/**
 * Rich-text note editor for the wrap-up workflow.
 *
 * Wraps `RichContentDocumentEditor` (the TinyMCE-backed editor used by the
 * legacy `AidaNoteEditor`) so reps get the full B/I/U/list/link toolbar.
 * The only MobX coupling is `inject()` for the `userSession` and
 * `impersonationContext` providers, both of which are mounted at the app
 * root, so this introduces zero new MobX dependency.
 *
 * Uncontrolled internally: the editor state is created once from the initial
 * `value` and the editor manages its own state thereafter, syncing back via
 * `onChange`. The wrap-up flow unmounts/remounts per lead, so a stale parent
 * `value` is a non-issue.
 *
 * IMPORTANT — `autoresizeToFitContent: true` is load-bearing. TinyMCE's
 * `min_height`/`max_height` settings are read by the **autoresize plugin**.
 * Without it loaded, those settings are ignored and the iframe falls back to
 * `height: 100%` which resolves against the parent — fragile inside a flex
 * column whose parent is `overflowY: auto` (no determinate height). The
 * symptom that pushed us here was a visible toolbar + visible empty content
 * area that did not respond to clicks: the iframe body had effectively zero
 * usable height even though the chrome rendered. Loading autoresize lets
 * TinyMCE manage iframe height in JS so the body always has real area.
 */
export const NoteEditor: FC<NoteEditorProps> = ({
	value,
	onChange,
	placeholder = 'Notes from this call…',
	disabled = false,
	minHeight = 160,
}) => {
	const initialStateRef = useRef<IRichContentEditorState>(createRichContentEditorStateWithText(value));

	return (
		<div className={css(styles.wrap)}>
			<div className={css(styles.editorContainer)}>
				<RichContentDocumentEditor
					autoFocus={!disabled}
					className='wrapup-note-editor'
					config={{
						autoresizeToFitContent: true,
						contentHorizontalPadding: 14,
						contentRawCss: NOTE_EDITOR_CONTENT_CSS,
						minHeight,
						maxHeight: 370,
						plugins: 'emoticons',
						toolbar: ToolbarLight,
					}}
					contentPlaceholderText={placeholder}
					contentState={initialStateRef.current}
					onContentStateChanged={(state: IRichContentEditorState) => {
						const html = getContentHtmlStringValueFromRawRichTextContentState(state.getRawRichTextContent());
						onChange(html);
					}}
					readOnly={disabled}
				/>
			</div>
		</div>
	);
};

const styles = StyleSheet.create({
	// Mirrors legacy `AidaNoteEditor/styles.ts:noteEditorContainer` — the
	// outer flex column. `flexGrow: 1` (NOT `flex: 1` / flex-basis: 0) so the
	// container's intrinsic content height is honored when the parent isn't a
	// flex container with determinate height (our case: `WrapupPanel.body` is
	// `overflowY: auto` and not a flex container).
	wrap: {
		display: 'flex',
		flexDirection: 'column',
		flexGrow: 1,
		width: '100%',
		border: '1px solid var(--border)',
		borderRadius: 8,
		background: 'var(--bg)',
		// Match legacy `:nth-child(1n) .tox-editor-header` — toolbar gets only
		// a bottom border (the wrap's own border draws the top edge). Avoid
		// `overflow: hidden` here: that has been observed to interfere with
		// iframe pointer-event hit testing inside flex columns.
		':nth-child(1n) .tox-editor-header': {
			borderBottom: '1px solid var(--border)',
		},
	},
	// Mirror of legacy `editorContainer`: `flexGrow: 1, minHeight: 50,
	// position: relative`. The `position: relative` matters because
	// `RichContentDocumentEditor` uses absolutely-positioned descendants
	// (loading spinner + chooser modals) that anchor against this box.
	editorContainer: {
		flexGrow: 1,
		minHeight: 50,
		position: 'relative',
	},
});
